'use client'
// カート関連で、ec-front全体共通で使うロジックを記述する

import { useCallback, useState } from 'react'
import { colorType } from '../color/type'
import { itemOptionType, itemType } from '../item/server'
import { serviceType } from '../service/server'
import { sizeType } from '../size/type'
import { handleUserBehavior } from '../user_behavior/client'
import { addCart, addCartWithOption, cartType, getCartItems } from './server'

// 計４種類のカート処理を提供する
// 振る舞い２種類と、パラメータ２種類の組み合わせ
// 振る舞い(Behavior)は、購入(Buy) or カートに追加(AddCart)
// パラメータ(AddCartParams)は、サイズとカラー(SizeColors) or オプション(ItemOptions)

type SubmitParamsSizeColors = {
  sizeColors: { size: sizeType; color: colorType }[]
}
type SubmitParamsItemOptions = {
  itemOptions: itemOptionType[]
}
type SubmitBase<T> = (params: T) => Promise<void>

type AddCartParamsBase<T> = {
  service: serviceType
  item: itemType
} & T
type AddCartBase<T> = (params: AddCartParamsBase<T>) => Promise<void>

type UseBehaviorBase<T> = ({
  service,
  item,
  addCartFunc,
}: {
  service: serviceType
  item: itemType
  addCartFunc: AddCartBase<T>
}) => [boolean, SubmitBase<T>]

// サイズとカラーで購入共通処理
const addCartSizeColors: AddCartBase<SubmitParamsSizeColors> = async ({
  service,
  item,
  sizeColors,
}: AddCartParamsBase<SubmitParamsSizeColors>) => {
  for (const sizeColor of sizeColors) {
    await addCart(
      service.service_id,
      item.item_id,
      sizeColor.size,
      sizeColor.color,
    )
  }
  // await addMultipleOptionsToCart(
  //   service.service_id,
  //   item.item_id,
  //   sizeColors.map((sc) => ({
  //     size: sc.size,
  //     color: sc.color,
  //   })),
  //   [],
  // )
  await handleUserBehavior(item.item_id, 'added_to_cart', item.price)
}

// オプションで購入共通処理
const addCartItemOptions: AddCartBase<SubmitParamsItemOptions> = async ({
  service,
  item,
  itemOptions,
}: AddCartParamsBase<SubmitParamsItemOptions>) => {
  for (const itemOption of itemOptions) {
    const option =
      itemOption.custom_option_id === 0
        ? createOptionFromCartItem(
            itemOption.name,
            itemOption.color ? itemOption.color.name : '',
            itemOption.size ? itemOption.size.name : '',
          )
        : undefined
    await addCartWithOption(
      service.service_id,
      item.item_id,
      itemOption.custom_option_id,
      option,
    )
  }
  await handleUserBehavior(item.item_id, 'added_to_cart', item.price)
}

// 購入共通処理
const useBuyBehavior = <T>({
  service,
  item,
  addCartFunc,
}: {
  service: serviceType
  item: itemType
  addCartFunc: AddCartBase<T>
}): [boolean, SubmitBase<T>] => {
  const [loading, setLoading] = useState(false)

  const submit = useCallback(
    async (params: T) => {
      setLoading(true)
      try {
        await addCartFunc({ service, item, ...params })
        const cartItems = await getCartItems(service.service_id)
        if (!cartItems || (cartItems && cartItems.length == 0)) {
          alert('カートに追加できませんでした。')
        } else if (cartItems.length == 1) {
          await Promise.all(
            cartItems.map((cartItem) =>
              handleUserBehavior(
                cartItem.item.item_id,
                'checkout_in',
                cartItem.item.price,
                cartItem.quantity,
              ),
            ),
          )
          window.location.href = '/transaction/address'
          return
        } else {
          window.location.href = '/transaction/address'
          return
        }
      } catch (error) {
        alert('カートに追加できませんでした。')
      }

      setLoading(false)
    },
    [service, item, addCartFunc],
  )

  return [loading, submit]
}

// カートに追加共通処理
const useAddCartBehavior = <T>({
  service,
  item,
  addCartFunc,
}: {
  service: serviceType
  item: itemType
  addCartFunc: AddCartBase<T>
}): [boolean, SubmitBase<T>] => {
  const [loading, setLoading] = useState(false)

  const submit = useCallback(
    async (params: T) => {
      setLoading(true)
      try {
        await addCartFunc({ service, item, ...params })
      } catch (error) {
        alert('カートに追加できませんでした。')
      }
      setLoading(false)
    },
    [service, item, addCartFunc],
  )

  return [loading, submit]
}

const defineUseSubmit = <T>(
  useBehavior: UseBehaviorBase<T>,
  addCartFunc: AddCartBase<T>,
) => {
  return ({ service, item }: { service: serviceType; item: itemType }) => {
    return useBehavior({
      service,
      item,
      addCartFunc,
    })
  }
}

// サイズとカラーで購入
export const useBuySizeColors = defineUseSubmit(
  useBuyBehavior,
  addCartSizeColors,
)

// サイズとカラーでカートに追加
export const useAddCartSizeColors = defineUseSubmit(
  useAddCartBehavior,
  addCartSizeColors,
)

// オプションで購入
export const useBuyItemOptions = defineUseSubmit(
  useBuyBehavior,
  addCartItemOptions,
)

// オプションでカートに追加
export const useAddCartItemOptions = defineUseSubmit(
  useAddCartBehavior,
  addCartItemOptions,
)

export const calculateTotalDiscount = (cartItem: cartType): number => {
  return cartItem.bulk_discount + cartItem.coupon_discount
}

export const createOptionFromCartItem = (
  name: string,
  color: string,
  size: string,
) => {
  let option = name
  if (color) {
    if (option != '') {
      option += '/'
    }
    option += `${color}`
  }
  if (size) {
    if (option != '') {
      option += '/'
    }
    option += ` ${size}`
  }
  if (option == '') {
    option = '/'
  }
  return option
}
